import { packFileFor, parseCfg, WARNING_FLAGS, type CfgFlag, type CfgStatement } from "@69ly/shared";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyButton } from "./CopyButton.tsx";
import { Icon } from "./Icon.tsx";

/** The words of a statement as the file has them, quotes where needed. */
export function statementText(s: CfgStatement): string {
  return [s.command, ...s.args.map((a) => (/[\s;]/.test(a) || a === "" ? `"${a}"` : a))].join(" ");
}

function FlagTag({ flag, s }: { flag: CfgFlag; s: CfgStatement }) {
  const { t } = useTranslation();
  const warn = WARNING_FLAGS.includes(flag);
  return <span className={`cfg-flag flag-${flag}${warn ? " is-warn" : ""}`}>{t(`cfg.flag.${flag}`, { key: (s.key ?? "").toUpperCase() })}</span>;
}

/** Where an exec line goes: a file of this pack (opens it), or one the pack does not have. */
function ExecTarget({ arg, pack }: { arg: string | undefined; pack: { names: string[]; open: (name: string) => void } }) {
  const { t } = useTranslation();
  const found = packFileFor(arg, pack.names);
  if (found)
    return (
      <button type="button" className="link-button small cfg-exec-link" onClick={() => pack.open(found)}>
        {t("cfg.openPackFile", { name: found })}
      </button>
    );
  return <span className="muted small cfg-exec-link">{t("cfg.notInPack")}</span>;
}

/** The author's explanation for a command, edited in place. */
function OwnExplain({ s, onSave }: { s: CfgStatement; onSave: (command: string, text: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(s.source === "author" ? (s.explain ?? "") : "");
  if (!open)
    return (
      <button type="button" className={`link-button small cfg-own${s.explain && s.source !== "author" ? " is-optional" : ""}`} onClick={() => setOpen(true)}>
        {s.source === "author" ? t("cfg.editOwn") : t("cfg.addOwn")}
      </button>
    );
  return (
    <form
      className="cfg-own-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(s.command.toLowerCase(), draft.trim());
        setOpen(false);
      }}
    >
      <input autoFocus value={draft} maxLength={300} onChange={(e) => setDraft(e.target.value)} placeholder={t("cfg.ownPlaceholder")} aria-label={t("cfg.ownLabel", { command: s.command })} />
      <button type="submit" className="btn btn-secondary btn-small">
        {t("cfg.saveOwn")}
      </button>
      <button type="button" className="btn btn-quiet btn-small" onClick={() => setOpen(false)}>
        {t("common.cancel")}
      </button>
    </form>
  );
}

/**
 * A config as a table: each command, its value and what it does, with what
 * deserves a look flagged. Comments in the file show as the author's notes.
 */
export function CfgTable({
  text,
  overrides,
  onOverride,
  pack,
}: {
  text: string;
  overrides?: Record<string, string>;
  onOverride?: (command: string, text: string) => void;
  /** The files of a pack: an exec line opens the file it runs. */
  pack?: { names: string[]; open: (name: string) => void };
}) {
  const { t } = useTranslation();
  const [onlyWarnings, setOnlyWarnings] = useState(false);
  const lines = useMemo(() => parseCfg(text, overrides), [text, overrides]);
  const flagged = lines.filter((l) => l.statements.some((s) => s.flags.some((f) => WARNING_FLAGS.includes(f))));
  const shown = (onlyWarnings ? flagged : lines).filter((l) => l.statements.length > 0 || l.comment);

  return (
    <div className="cfg-table-wrap">
      <div className="cfg-table-bar">
        <span className="small muted">{t("cfg.counts", { count: lines.filter((l) => l.statements.length > 0).length, warnings: flagged.length })}</span>
        {flagged.length > 0 && (
          <label className="check small">
            <input type="checkbox" checked={onlyWarnings} onChange={(e) => setOnlyWarnings(e.target.checked)} />
            {t("cfg.onlyWarnings")}
          </label>
        )}
      </div>
      <div className="cfg-scroll">
        <table className="cfg-table">
          <thead>
            <tr>
              <th scope="col" className="cfg-n">
                #
              </th>
              <th scope="col">{t("cfg.command")}</th>
              <th scope="col">{t("cfg.value")}</th>
              <th scope="col">{t("cfg.what")}</th>
              <th scope="col">
                <span className="visually-hidden">{t("cfg.copyLine")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((l) =>
              l.statements.length === 0 ? (
                <tr key={l.line} className="cfg-note">
                  <td className="cfg-n">{l.line}</td>
                  <td colSpan={4}>
                    <Icon name="tag" size={12} /> {l.comment}
                  </td>
                </tr>
              ) : (
                l.statements.map((s, i) => {
                  const warn = s.flags.some((f) => WARNING_FLAGS.includes(f));
                  return (
                    <tr key={`${l.line}-${i}`} className={`${warn ? "is-warn" : ""}${s.flags.includes("unknown") ? " is-unknown" : ""}`}>
                      <td className="cfg-n">{i === 0 ? l.line : ""}</td>
                      <td className="mono cfg-cmd">{s.command}</td>
                      <td className="mono cfg-val">{s.command.toLowerCase() === "bind" ? `${s.args[0] ?? ""} → ${s.args.slice(1).join(" ")}` : s.args.join(" ")}</td>
                      <td>
                        {s.explain ? (
                          <span className={s.source === "author" ? "cfg-author" : s.source === "family" ? "cfg-family" : undefined}>{s.explain}</span>
                        ) : (
                          <span className="muted">{t("cfg.notRecognised")}</span>
                        )}
                        {i === 0 && l.comment && <span className="cfg-comment"> · {l.comment}</span>}
                        {s.flags.length > 0 && (
                          <span className="cfg-flags">
                            {s.flags
                              .filter((f) => f !== "unknown")
                              .map((f) => (
                                <FlagTag key={f} flag={f} s={s} />
                              ))}
                          </span>
                        )}
                        {pack && s.command.toLowerCase() === "exec" && <ExecTarget arg={s.args[0]} pack={pack} />}
                        {onOverride && <OwnExplain s={s} onSave={onOverride} />}
                      </td>
                      <td className="cfg-copy">
                        <CopyButton text={statementText(s)} compact />
                      </td>
                    </tr>
                  );
                })
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
